import { CallLogFileHandler, CallLogParseResult } from '../../models/callLogFileHandler';
import { read, utils, WorkSheet } from 'xlsx';
import { parse, ParseResult } from 'papaparse';
import { ParsedCallLogXlsFileSheet } from '../../store/state/createJobState';
import { getCallLogFileId } from '../../store/actions/createJobActions';
import { getUid } from '../ids';

export const xlsxCallLogFileHandler: CallLogFileHandler = {
	canParse: (extension, mime) => {
		switch (extension) {
			case 'xls':
			case 'xlsx':
				return true;
		}

		switch (mime) {
			case 'application/vnd.ms-excel':
			case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
				return true;
		}
		
		return false;
	},
	parse: file => {
		return new Promise<CallLogParseResult>((resolve) => {
			const reader = new FileReader();
			
			reader.onload = (e) => {
				if (!e.target || !e.target.result || typeof e.target.result === 'string') {
					resolve({type: 'error', error: { type: 'parseFailed' }});
					return;
				}
				
				try {
					const data = new Uint8Array(e.target.result);
					const workbook = read(data, {type: 'array'});
					const promises = workbook.SheetNames.map(name => {
						return getParsedWorksheet(name, workbook.Sheets[name]);
					});
					
					Promise.all(promises).then(worksheetResults => {
						let sheets: ParsedCallLogXlsFileSheet[] = [],
							errorCount = 0;
						
						worksheetResults.forEach(result => {
							if (result.type === 'success') {
								sheets.push(result.sheet);
							} else {
								errorCount++;
							}
						});
						
						if (!sheets.length) {
							resolve({
								type: 'error',
								error: errorCount > 0
									? { type: 'parseFailed' }
									: { type: 'empty' }
							});
						} else {
							resolve({
								type: 'success',
								file: {
									type: 'xls',
									filename: file.name,
									id: getCallLogFileId(),
									selectedSheet: sheets[0],
									allSheets: sheets
								},
								warningMessage: errorCount > 0
									? `Failed to parse ${errorCount.toLocaleString()} sheet${errorCount === 1 ? '' : 's'}`
									: null
							});
						}
					}, () => {
						// this shouldn't ever happen because the promises should always
						// resolve - it will just resolve with an error if something went wrong.
						resolve({type: 'error', error: { type: 'parseFailed' }});
					});
				} catch (e) {
					resolve({type: 'error', error: { type: 'parseFailed' }});
				}
			};
			
			reader.onerror = () => {
				resolve({type: 'error', error: { type: 'parseFailed' }});
			};
			
			reader.readAsArrayBuffer(file);
		});
	}
}

function getParsedWorksheet(name: string, worksheet: WorkSheet): Promise<{ type: 'success'; sheet: ParsedCallLogXlsFileSheet; } | { type: 'error'; }> {
	return new Promise(resolve => {
		try {
			// bit of indirection here in that we go to a csv and then
			// to the final data format but it is seems to be the best
			// option in that it lets the library take care of all the hard
			// stuff in terms of parsing worksheets
			const csv = utils.sheet_to_csv(worksheet, { blankrows: false });

			parse<string[]>(csv, {
				header: false,
				skipEmptyLines: true,
				complete: (results: ParseResult<string[]>) => {
					resolve({
						type: 'success',
						sheet: { 
							sheetId: getUid(),
							name,
							data: { rows: results.data ?? [] }
						}
					});
				},
				error: () => {
					resolve({type: 'error'});
				}
			});
		} catch (e) {
			resolve({type: 'error'});
		}
	});
}
